// =============================================================================
// Reports data layer — calls the Postgres RPC aggregation functions.
// =============================================================================
import { supabase } from './supabase/client'

export interface SalesSummaryRow { day: string; invoice_count: number; total_sales: number; total_tax: number; avg_ticket: number; total_qty: number; taxable_sales: number; non_taxable_sales: number }
export interface HourlyRow { hour: number; invoice_count: number; total_sales: number; total_tax: number; avg_ticket: number; total_qty: number }
export interface ProductRow { product_name: string; units: number; revenue: number; cost: number; profit: number; avg_price: number }
export interface PaymentRow { payment_method: string; payment_count: number; total_amount: number; avg_amount: number }
export interface TypeRow { inventory_type: string; units: number; revenue: number; cost: number; profit: number; avg_price: number }
export interface MonthlyRow { month: string; invoice_count: number; total_sales: number }

export async function salesSummary(start: string, end: string): Promise<SalesSummaryRow[]> {
  const { data, error } = await supabase.rpc('report_sales_summary', { start_date: start, end_date: end })
  if (error) throw error
  return (data ?? []) as SalesSummaryRow[]
}

export async function hourlySales(day: string): Promise<HourlyRow[]> {
  const { data, error } = await supabase.rpc('report_hourly_sales', { target_day: day })
  if (error) throw error
  return (data ?? []) as HourlyRow[]
}

export async function productSales(start: string, end: string, limit = 50): Promise<ProductRow[]> {
  const { data, error } = await supabase.rpc('report_product_sales', { start_date: start, end_date: end, row_limit: limit })
  if (error) throw error
  return (data ?? []) as ProductRow[]
}

export async function paymentBreakdown(day: string): Promise<PaymentRow[]> {
  const { data, error } = await supabase.rpc('report_payment_breakdown', { target_day: day })
  if (error) throw error
  return (data ?? []) as PaymentRow[]
}

export async function typeSales(start: string, end: string): Promise<TypeRow[]> {
  const { data, error } = await supabase.rpc('report_type_sales', { start_date: start, end_date: end })
  if (error) throw error
  return (data ?? []) as TypeRow[]
}

export async function monthlyTrend(monthsBack = 12): Promise<MonthlyRow[]> {
  const { data, error } = await supabase.rpc('report_monthly_trend', { months_back: monthsBack })
  if (error) throw error
  return (data ?? []) as MonthlyRow[]
}

// ---- Legacy report catalog (migration 007) ---------------------------------

export interface DeptRow { department: string; units: number; revenue: number; cost: number; profit: number }
export interface CatRow { category: string; units: number; revenue: number; cost: number; profit: number }
export interface ProductSummaryRow { product_name: string; units: number; revenue: number; cost: number; profit: number }
export interface CashierRow { clerk_id: string; invoice_count: number; total_sales: number; total_tax: number }
export interface HouseRow { customer: string; invoice_count: number; total_sales: number }
export interface DayTotalsRow { gross_sales: number; total_tax: number; invoice_count: number; payout_total: number; refund_total: number; taxable_sales: number; non_taxable_sales: number; net_sales: number; avg_ticket: number; total_qty: number; avg_tax: number }
export interface SalesTaxRow { day: string; taxable: number; non_taxable: number; tax: number; total: number }
export interface RefundSummaryRow { day: string; refund_count: number; total_qty: number; total_refunded: number; total_tax: number; avg_refund: number }
export interface PayoutSummaryRow { day: string; payout_count: number; total_amount: number; avg_amount: number }
export interface OutSaleRow { day: string; units: number; revenue: number; invoice_count: number; cost: number; profit: number }
export interface InventoryValuationRow { inventory_type: string; item_count: number; on_hand: number; cost_value: number; retail_value: number; avg_cost: number; avg_retail: number; markup_pct: number }
export interface LowStockRow { product_name: string; inventory_type: string; on_hand: number; reorder_level: number; suggested_qty: number; retail_price: number; vendor: string }
export interface JobCostingRow { month: string; job_count: number; revenue: number; cost: number; profit: number }
export interface JobProfitTypeRow { item_type: string; units: number; revenue: number; cost: number; profit: number }
export interface GiftCertSummaryRow { status: string; cert_count: number; issued_amount: number; outstanding_balance: number; redeemed_amount: number; used_count: number }
export interface ProfitLossRow { line: string; revenue: number; cost: number; profit: number }
export interface ProfitabilityDesignerRow { designer: string; job_count: number; revenue: number; cost: number; profit: number }

async function rpcRange<T>(fn: string, start: string, end: string, extra: Record<string, unknown> = {}): Promise<T[]> {
  const { data, error } = await supabase.rpc(fn, { start_date: start, end_date: end, ...extra })
  if (error) throw error
  return (data ?? []) as T[]
}

export const saleByDepartment = (s: string, e: string) => rpcRange<DeptRow>('report_sale_by_department', s, e)
export const saleByCategory = (s: string, e: string) => rpcRange<CatRow>('report_sale_by_category', s, e)
export const productSaleSummary = (s: string, e: string, limit = 200) => rpcRange<ProductSummaryRow>('report_product_sale_summary', s, e, { row_limit: limit })
export const saleByCashier = (s: string, e: string) => rpcRange<CashierRow>('report_sale_by_cashier', s, e)
export const houseWiseSale = (s: string, e: string, limit = 200) => rpcRange<HouseRow>('report_house_wise_sale', s, e, { row_limit: limit })
export const xoutReport = (s: string, e: string) => rpcRange<PaymentRow>('report_xout', s, e)
export const salesTaxReport = (s: string, e: string) => rpcRange<SalesTaxRow>('report_sales_tax', s, e)
export const refundSummary = (s: string, e: string) => rpcRange<RefundSummaryRow>('report_refund_summary', s, e)
export const payoutSummary = (s: string, e: string) => rpcRange<PayoutSummaryRow>('report_payout_summary', s, e)
export const outSaleDatewise = (s: string, e: string) => rpcRange<OutSaleRow>('report_outsale_datewise', s, e)
export const jobCosting = (s: string, e: string) => rpcRange<JobCostingRow>('report_job_costing', s, e)
export const jobProfitByType = (s: string, e: string) => rpcRange<JobProfitTypeRow>('report_job_profit_by_type', s, e)
export const profitLoss = (s: string, e: string) => rpcRange<ProfitLossRow>('report_profit_loss', s, e)
export const profitabilityByDesigner = (s: string, e: string) => rpcRange<ProfitabilityDesignerRow>('report_profitability_by_designer', s, e)

// ---- IMS appointment / designer / spring-cleanup reports (migration 018) ----
export interface ApptMonthRow { month: string; appt_count: number; go_ahead_count: number; no_go_count: number; conversion_pct: number }
export interface ApptZipRow { zip: string; city: string; appt_count: number; go_ahead_count: number; conversion_pct: number }
export interface DesignerScheduleRow { designer: string; appt_date: string; appt_time: string; customer: string; city: string }
export interface SpringCleanupRow { bill_code: string; customer: string; bill_date: string; invoice_total: number }

export const appointmentsByMonth = (s: string, e: string) => rpcRange<ApptMonthRow>('report_appointments_by_month', s, e)
export const appointmentsByZip = (s: string, e: string) => rpcRange<ApptZipRow>('report_appointments_by_zip', s, e)
export const designerSchedule = (s: string, e: string) => rpcRange<DesignerScheduleRow>('report_designer_schedule', s, e)
export const springCleanup = (s: string, e: string) => rpcRange<SpringCleanupRow>('report_spring_cleanup', s, e)

// ---- IMS job-total reports (migration 019) ----------------------------------
export interface JobTotalsDesignerRow { designer: string; job_count: number; invoice_count: number; total: number; avg_job: number }
export interface JobTotalsForemanRow { foreman: string; job_count: number; invoice_count: number; total: number; avg_job: number }
export interface EstInvCategoryRow { category: string; estimate_count: number; estimate_total: number; invoice_count: number; invoice_total: number; variance: number }

export const jobTotalsByDesigner = (s: string, e: string) => rpcRange<JobTotalsDesignerRow>('report_job_totals_by_designer', s, e)
export const jobTotalsByForeman = (s: string, e: string) => rpcRange<JobTotalsForemanRow>('report_job_totals_by_foreman', s, e)
export const estInvByCategory = (s: string, e: string) => rpcRange<EstInvCategoryRow>('report_est_inv_by_category', s, e)

export async function dayTotals(day: string): Promise<DayTotalsRow | null> {
  const { data, error } = await supabase.rpc('report_day_totals', { target_day: day })
  if (error) throw error
  const rows = (data ?? []) as DayTotalsRow[]
  return rows[0] ?? null
}

export async function inventoryValuation(): Promise<InventoryValuationRow[]> {
  const { data, error } = await supabase.rpc('report_inventory_valuation')
  if (error) throw error
  return (data ?? []) as InventoryValuationRow[]
}

export async function lowStock(threshold = 5): Promise<LowStockRow[]> {
  const { data, error } = await supabase.rpc('report_low_stock', { threshold })
  if (error) throw error
  return (data ?? []) as LowStockRow[]
}

export async function giftCertificateSummary(): Promise<GiftCertSummaryRow[]> {
  const { data, error } = await supabase.rpc('report_gift_certificate_summary')
  if (error) throw error
  return (data ?? []) as GiftCertSummaryRow[]
}

// Convert any report rows to CSV and trigger download
export function downloadCsv(filename: string, rows: Record<string, unknown>[]) {
  if (!rows.length) return
  const headers = Object.keys(rows[0])
  const csv = [
    headers.join(','),
    ...rows.map(r => headers.map(h => {
      const v = r[h]
      const s = v == null ? '' : String(v)
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }).join(',')),
  ].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
